import React, { useState } from "react";
import axios from "axios";
import { Instagram, Music2, Mail } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      await axios.post(`${API}/newsletter`, { email });
      setState("success");
      setEmail("");
    } catch {
      setState("error");
    }
  };

  const EXPLORE = [
    { href: "#services", key: "nav.services" },
    { href: "#about", key: "nav.about" },
    { href: "#how-it-works", key: "nav.how" },
    { href: "#faq", key: "nav.faq" },
    { href: "#disclaimer", key: "footer.agreement" },
    { href: "#contact", key: "footer.contact" },
    { href: "/clinic", key: "footer.clinics" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#3F5043] text-[#F8F7F3] pt-24 pb-10 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-[#F8F7F3]/15">
          <div className="md:col-span-5">
            <div className="font-serif italic text-4xl text-[#F8F7F3] mb-3">
              Soarlife
            </div>
            <p className="text-[#F8F7F3]/70 max-w-sm leading-relaxed">
              {t("footer.tagline")}
            </p>

            <div className="mt-6 space-y-1.5 text-sm">
              <div>
                <span className="text-[#F8F7F3]/55 text-xs uppercase tracking-[0.2em] font-semibold mr-2">
                  {t("contact.label.call")}
                </span>
                <a
                  href="tel:+14073749834"
                  data-testid="footer-call"
                  className="text-[#F8F7F3] hover:text-[#AE8F78] transition-colors"
                >
                  (407) 374-9834
                </a>
              </div>
              <div>
                <span className="text-[#F8F7F3]/55 text-xs uppercase tracking-[0.2em] font-semibold mr-2">
                  {t("contact.label.text")}
                </span>
                <a
                  href="sms:+18446632125"
                  data-testid="footer-text"
                  className="text-[#F8F7F3] hover:text-[#AE8F78] transition-colors"
                >
                  (844) 663-2125
                </a>
              </div>
              <div>
                <span className="text-[#F8F7F3]/55 text-xs uppercase tracking-[0.2em] font-semibold mr-2">
                  {t("contact.label.email")}
                </span>
                <a
                  href="mailto:contact@soarlife.consulting"
                  data-testid="footer-email"
                  className="text-[#F8F7F3] hover:text-[#AE8F78] transition-colors"
                >
                  contact@soarlife.consulting
                </a>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://instagram.com/soarlife.consulting"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                aria-label="Instagram @soarlife.consulting"
                className="w-10 h-10 rounded-full border border-[#F8F7F3]/30 flex items-center justify-center hover:bg-[#F8F7F3] hover:text-[#3F5043] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://tiktok.com/@soarlife.consulting"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tiktok"
                aria-label="TikTok @soarlife.consulting"
                className="w-10 h-10 rounded-full border border-[#F8F7F3]/30 flex items-center justify-center hover:bg-[#F8F7F3] hover:text-[#3F5043] transition-colors"
              >
                <Music2 size={16} />
              </a>
              <a
                href="mailto:contact@soarlife.consulting"
                data-testid="social-email"
                aria-label="Email"
                className="w-10 h-10 rounded-full border border-[#F8F7F3]/30 flex items-center justify-center hover:bg-[#F8F7F3] hover:text-[#3F5043] transition-colors"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.25em] text-[#F8F7F3]/60 font-semibold mb-5">
              {t("footer.explore")}
            </div>
            <ul className="space-y-3 text-[#F8F7F3]/85">
              {EXPLORE.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="hover:text-[#AE8F78]">
                    {t(l.key)}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/book"
                  className="hover:text-[#AE8F78]"
                  data-testid="footer-book-link"
                >
                  {t("footer.book")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[#F8F7F3]/60 font-semibold mb-5">
              {t("footer.newsletter.label")}
            </div>
            <p className="text-sm text-[#F8F7F3]/70 mb-4">
              {t("footer.newsletter.intro")}
            </p>
            {state === "success" ? (
              <div
                data-testid="newsletter-success"
                className="text-sm text-[#AE8F78]"
              >
                {t("footer.newsletter.success")}
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="newsletter-form"
                className="flex gap-2"
              >
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.newsletter.placeholder")}
                  className="flex-1 bg-transparent border-b border-[#F8F7F3]/30 py-2.5 px-1 text-[#F8F7F3] placeholder-[#F8F7F3]/40 focus:outline-none focus:border-[#AE8F78] transition-colors text-sm"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={state === "loading"}
                  className="px-5 py-2.5 rounded-full bg-[#AE8F78] text-[#F8F7F3] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60"
                >
                  {state === "loading" ? "…" : t("footer.newsletter.submit")}
                </button>
              </form>
            )}
            {state === "error" && (
              <div
                data-testid="newsletter-error"
                className="text-xs text-[#AE8F78] mt-2"
              >
                {t("footer.newsletter.error")}
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 text-xs text-[#F8F7F3]/55">
          <div>
            {t("footer.copyright").replace("{year}", new Date().getFullYear())}
          </div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#F8F7F3]">
              {t("footer.privacy")}
            </a>
            <a href="/admin" data-testid="footer-team-login" className="hover:text-[#F8F7F3]">
              {t("footer.teamlogin")}
            </a>
            <a href="#" className="hover:text-[#F8F7F3]">
              {t("footer.hipaa")}
            </a>
            <a href="#" className="hover:text-[#F8F7F3]">
              {t("footer.terms")}
            </a>
          </div>
        </div>

        <div className="mt-16 -mb-6 text-center">
          <div className="font-serif italic text-[18vw] sm:text-[14vw] leading-[0.85] text-[#F8F7F3]/8 select-none">
            soarlife
          </div>
        </div>
      </div>
    </footer>
  );
}
